import React from 'react';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import {
  fetchMarketingVenuesForCollection,
  Link,
  Loader,
  utils,
} from '@resy/ui-kitchen';
import moment from 'moment-timezone';
import Cookies from 'universal-cookie';
import uniqBy from 'lodash/uniqBy';
import {
  updateLinksWithOriginB2B,
  handleMktAnalytics,
  handle404Redirect,
  getIsFAQsHidden,
} from '../AmexMarketingUtils/AmexUtils';
import AmexGDADirHeader from './AmexGDADirHeader';
import AmexGDAFooter from '../AmexGDA/AmexGDAFooter';
import VenuesGrid from './VenuesGrid';
import CountryCitySelector from './CountryCitySelector';
import VenuesMapDisplay from './VenuesMapDisplay';
import '../AmexGDA/AmexGDA.scss';
import './AmexGDADir.scss';
import { getLocationConfig } from '../../../../../ui-kitchen/src/api';
import Logger from '../../../../../ui-kitchen/src/utils/logger';
import { sanitizeString } from '../../../../../ui-kitchen/src/utils/sanitizeString';
import { CTA_EVENT_NAME } from '../AmexMarketingUtils/constants';

const GDA_TYPE_ID = 10;
const GDA_COOKIE_NAME = 'GDA';
const GDA_MEMBER = 'GDAMember';
const GDA_ELIGIBLE = 'GDAEligible';
const HIGHLIGHTED = 'pgmDirHighlighted';
const BOTH = 'both';

class AmexGDADir extends React.Component {
  static propTypes = {
    ResyAmexConfig: PropTypes.shape({
      baseRequestUrl: PropTypes.string.isRequired,
    }).isRequired,
    Analytics: PropTypes.shape({
      event: PropTypes.func,
    }).isRequired,
    userLanguage: PropTypes.string,
    $location: PropTypes.shape({
      url: PropTypes.func,
      host: PropTypes.func,
      search: PropTypes.func,
    }).isRequired,
    collections: PropTypes.arrayOf(PropTypes.object),
    venues: PropTypes.shape(PropTypes.object),
    dispatch: PropTypes.func.isRequired,
    eventsHideStatus: PropTypes.bool.isRequired,
    userLocation: PropTypes.shape({
      lat: PropTypes.string,
      long: PropTypes.string,
      isNonUS: PropTypes.bool,
    }).isRequired,
  };

  static defaultProps = {
    userLanguage: navigator.languages ? navigator.languages[0] : (navigator.language || navigator.userLanguage),
    collections: [],
    venues: {},
  }

  cookies = new Cookies();

  state = {
    loading: true,
    cityCountryText: '',
    locations: [],
    staticLocations: [],
    dynamicLocations: [],
    selectedCity: '',
    venues: [],
    staticVenues: [],
    highlightedVenues: null,
    dynamicVenues: [],
    proximity: {
      cityCode: null,
      countryCode: null,
    },
    dataOutput: null,
    gdaCookie: '',
    hasOriginB2B: false,
    hideFAQs: false,
  };

  componentDidMount() {
    const currentHash = (window.location.hash).replace('#', '');
    window.location.hash = '';
    this.setGDAPageCookie();
    this.fetchDataWithConfig();
    this.combineLocations();
    this.fetchStaticVenues(currentHash);
    this.fetchHighlightedVenues();
    this.handleOriginB2B();
  }

  componentDidUpdate(props, state) {
    const { venues } = this.props;
    const { selectedCity } = this.state;

    const collectionId = this.getCollectionId();

    if (state.selectedCity !== selectedCity) {
      this.handleDynamicVenueData();
    } else if (selectedCity && collectionId) {
      const key = `${selectedCity}-${collectionId}`;
      if (props.venues[key]?.length !== venues[key].length) this.handleDynamicVenueData();
    }
  }

  setGDAPageCookie() {
    this.setState({ gdaCookie: this.cookies.get(GDA_COOKIE_NAME) });
  }

  getCollectionId = () => {
    const { collections } = this.props;
    const collection = collections.filter((val) => val.info.type_id === GDA_TYPE_ID);
    return collection[0]?.id || null;
  }

  handleDynamicVenueData = () => {
    const { venues } = this.props;
    const { selectedCity } = this.state;

    const collectionId = this.getCollectionId();
    const key = `${selectedCity}-${collectionId}`;
    const venuesByKey = venues[key];

    if (!venuesByKey) return;

    const dynamicVenues = venuesByKey?.map((item) => ({
      /* eslint-disable camelcase */
      city_code: item.venue.location?.code,
      city_name: item.venue.location?.name,
      name: item.venue.name,
      cuisine: item.venue.cuisine,
      currency: item.venue.currency,
      price_range: item.venue.priceRange,
      image: item.venue.images ? item.venue.images[0] : '',
      bookNowLink: `/cities/${item.venue.location.urlSlug}/venues/${item.venue.urlSlug}`,
      /* eslint-enable camelcase */
    }
    ));
    this.setState({
      dynamicVenues,
    });
  }

  handleOriginB2B = () => {
    const { $location } = this.props;
    const { origin } = $location.search();
    if (origin && origin === 'B2BPartner') this.setState({ hasOriginB2B: true });
  };

  fetchStaticLocations = async () => {
    const {
      ResyAmexConfig: {
        baseRequestUrl,
      },
    } = this.props;
    const assetsEndpoint = `${baseRequestUrl}HardcodedVenues/GDACountriesCities.json`;

    // fetch static venues and remove venues with empty names
    try {
      const response = await fetch(assetsEndpoint, {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json;charset=UTF-8',
          'Access-Control-Allow-Origin': baseRequestUrl,
        },
        mode: 'cors',
      });
      const staticLocationData = await response.json();
      const staticLocations = staticLocationData.cities.map((city) => ({
        // eslint-disable-next-line camelcase
        country_code: city.countryCode,
        // eslint-disable-next-line camelcase
        country_name: city.countryName,
        code: city.cityCode,
        name: city.cityName,
      }));

      this.setState({ staticLocations });
    } catch (err) {
      // show 'noAvailableRestaurants' when both static and dynamic locations are empty
      this.setState({ staticLocations: [] });
    }
  }

  fetchDynamicLocations = async () => {
    const { userLocation } = this.props;
    const { lat, long } = userLocation;

    try {
      const allLocations = await getLocationConfig({ lat, long });
      const dynamicLocations = allLocations.filter((location) => (location.has_rga_venues && location.show_on_web !== 0));
      const closestLocation = dynamicLocations.find((loc) => loc.has_rga_venues);
      const proximity = {
        cityCode: closestLocation.code,
        countryCode: closestLocation.country_code,
      };
      this.setState({
        dynamicLocations,
        proximity,
      });
    } catch (err) {
      Logger.error('[AmexGDADir] API Error:', err.status, err);
      // show 'noAvailableRestaurants' when both static and dynamic locations are empty
      this.setState({ dynamicLocations: [] });
    }
  }

  combineLocations = async () => {
    this.setState({ loading: true });
    await this.fetchStaticLocations();
    await this.fetchDynamicLocations();
    const { dynamicLocations, staticLocations } = this.state;

    const locations = [...dynamicLocations, ...staticLocations];
    this.setState({ locations, loading: false });
  }

  fetchStaticVenues = async (hash) => {
    const {
      ResyAmexConfig: {
        baseRequestUrl,
      },
    } = this.props;
    const assetsEndpoint = `${baseRequestUrl}HardcodedVenues/GDAVenues.json`;

    // fetch static venues and remove venues with empty names
    try {
      const response = await fetch(assetsEndpoint, {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json;charset=UTF-8',
          'Access-Control-Allow-Origin': baseRequestUrl,
        },
        mode: 'cors',
      });
      const venueData = await response.json();
      // don't show venues with missing fields ( skip this for venue_url and venue_url_text fields)
      // static venues images are stored in S3 now. Use baseRequestUrl in the image path
      const staticVenues = venueData.venues
        .filter((venue) => !Object.entries(venue).some(([k, v]) => ((k !== 'venue_url' && k !== 'venue_url_text') && !v)))
        .map((venue) => ({
          // eslint-disable-next-line camelcase
          city_code: venue.city_code,
          // eslint-disable-next-line camelcase
          city_name: venue.city_name,
          cuisine: venue.cuisine,
          currency: venue.currency,
          image: `${baseRequestUrl}HardcodedVenues/${venue.image}`,
          name: venue.name,
          // eslint-disable-next-line camelcase
          price_range: venue.price_range,
          // eslint-disable-next-line camelcase
          venue_url: venue.venue_url || '',
          // eslint-disable-next-line camelcase
          venue_url_text: venue.venue_url_text || '',
        }));

      this.setState({ staticVenues });
      window.location.hash = hash;
    } catch (err) {
      // set empty venues array
      // show 'noAvailableVenues' when both static and dynamic venues are empty
      this.setState({ staticVenues: [] });
    }
  }

  fetchHighlightedVenues = async () => {
    const {
      ResyAmexConfig: {
        baseRequestUrl,
      },
      userLanguage,
    } = this.props;
    const assetsEndpoint = `${baseRequestUrl}FeaturedVenues/GDAFeaturedVenues.json`;

    // fetch highlighted venues
    try {
      const response = await fetch(assetsEndpoint, {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json;charset=UTF-8',
          'Access-Control-Allow-Origin': baseRequestUrl,
        },
        mode: 'cors',
      });
      const featuredData = await response.json();

      let highlightedVenuesData = userLanguage === 'en-GB' ? featuredData.featuredUK.restaurants : featuredData.featuredUS.restaurants;

      // remove pgm_dir_slot_position with null or zero values
      highlightedVenuesData = highlightedVenuesData.filter((obj) => (obj.pgm_dir_slot_position !== undefined && obj.pgm_dir_slot_position !== 0 && obj.pgm_dir_slot_position !== null));

      // remove duplicate pgm_dir_slot_position values
      highlightedVenuesData = uniqBy(highlightedVenuesData, 'pgm_dir_slot_position');

      // remove if pgm_dir_slot_position is not between 1 & 5
      const slotValuesArr = [1, 2, 3, 4, 5];
      const newHighlightedVenuesData = [];
      highlightedVenuesData.forEach((hVenue) => {
        if (slotValuesArr.includes(hVenue.pgm_dir_slot_position)) {
          newHighlightedVenuesData.push(hVenue);
        }
      });

      const highlightedVenuesArr = newHighlightedVenuesData
        .filter((venue) => !Object.entries(venue).some(([k, v]) => ((k !== 'bookNowLink') && !v)))
        .reduce((highlightedVenues, item) => {
          if ((item.venue_type === HIGHLIGHTED || item.venue_type === BOTH)) {
            highlightedVenues.push(item);
          }
          return highlightedVenues;
        }, []);

      const highlightedVenuesArrOrdered = highlightedVenuesArr.sort((a, b) => a.pgm_dir_slot_position - b.pgm_dir_slot_position);
      this.setState({ highlightedVenues: highlightedVenuesArrOrdered });
    } catch (err) {
      this.setState({ highlightedVenues: null });
    }
  }

  fetchDynamicVenues = async (selectedCity) => {
    const { venues, dispatch } = this.props;
    const collectionId = this.getCollectionId();

    if (!selectedCity || !collectionId) return;

    /* eslint-disable camelcase */
    const payload = {
      location_id: selectedCity,
      collection_id: collectionId,
      day: moment().format('YYYY-MM-DD'),
      party_size: 2,
      finder: 4,
    };
    /* eslint-enable camelcase */

    const key = `${selectedCity}-${collectionId}`;
    if (!venues[key]) {
      try {
        await dispatch(fetchMarketingVenuesForCollection(payload));
      } catch (err) {
        // set empty venues array
        // show 'noAvailableVenues' when both static and dynamic venues are empty
        this.setState({ dynamicVenues: [] });
      }
    }
  }

  updateVenues = async (selectedCity) => {
    this.setState({ loading: true });
    // call collections endpoint
    await this.fetchDynamicVenues(selectedCity);
    const { staticVenues, dynamicVenues } = this.state;
    // filter static list by city
    const filteredStaticVenues = staticVenues.filter((venue) => venue.city_code === selectedCity);
    const mergedVenuesList = [...dynamicVenues, ...filteredStaticVenues];
    // filter the venue list by selected city, sort by name
    const venues = mergedVenuesList
      .reduce((unique, current) => {
        const filteredMergedVenue = unique.find((venue) => venue.name.toLowerCase() === current.name.toLowerCase());
        if (!filteredMergedVenue) { unique.push(current); } return unique;
      }, [])
      .sort((current, next) => (new Intl.Collator(undefined, {
        numeric: true,
        sensitivity: 'base',
      }).compare(current.name, next.name)));
    this.setState({
      venues,
      loading: false,
    });
  }

  callCity = (selectedCity, cityCountryText) => {
    this.setState({
      selectedCity,
      cityCountryText,
      venues: [],
      dynamicVenues: [],
    });

    if (selectedCity) {
      this.updateVenues(selectedCity);
    }
  }

  fetchConfig = async () => {
    const {
      ResyAmexConfig: {
        baseRequestUrl,
      },
    } = this.props;

    let configData;
    const gdaConfigEndPoint = `${baseRequestUrl}GDA/GDAConfig.json`;
    try {
      const configResponse = await fetch(gdaConfigEndPoint, {
        method: 'GET',
      });
      configData = await configResponse.json();
    } catch (err) {
      Logger.error('[AmexGDADir] Error:', err);
    }
    return configData;
  }

  fetchDataWithConfig = () => {
    const configPromise = this.fetchConfig();

    configPromise.then((configResponse) => {
      if (configResponse) {
        /* eslint camelcase: ["error", {ignoreDestructuring: true}] */
        // eslint-disable-next-line camelcase
        const {
          // eslint-disable-next-line camelcase
          is_page_enabled = true,
          // eslint-disable-next-line camelcase
          gda_languages_permitted = 'en-US',
        } = configResponse;

        // eslint-disable-next-line camelcase
        if (!is_page_enabled) {
          handle404Redirect();
        }
        // eslint-disable-next-line camelcase
        if (gda_languages_permitted) {
          this.fetchDataFile(gda_languages_permitted);
        }
      }
    });
  }

  fetchDataFile = async (gdaLanguagesPermitted) => {
    const {
      userLanguage,
      ResyAmexConfig: {
        baseRequestUrl,
      },
    } = this.props;

    let dataOutput;
    let fileName;
    let hideFAQs;
    const langArr = gdaLanguagesPermitted.split(',');

    const match = langArr.find((element) => {
      if (element === userLanguage) {
        return true;
      }
      return false;
    });

    if (match) {
      fileName = `GDA_${userLanguage}.json`;
    } else {
      fileName = 'GDA_en-US.json';
    }

    const gdaDataEndPoint = `${baseRequestUrl}GDA/${fileName}`;
    try {
      const dataResponse = await fetch(gdaDataEndPoint, {
        method: 'GET',
      });
      dataOutput = await dataResponse.json();
      hideFAQs = getIsFAQsHidden(dataOutput?.faqs);
    } catch (err) {
      Logger.error('[AmexGDADir] Error:', err);
    }
    this.setState({ dataOutput, hideFAQs });
    return dataOutput;
  }

  handleDirTagging = (extraParams) => {
    const { Analytics: { event } } = this.props;
    handleMktAnalytics(event, CTA_EVENT_NAME, extraParams);
  }

  render() {
    const {
      loading,
      cityCountryText,
      locations,
      selectedCity,
      venues,
      proximity,
      dataOutput,
      gdaCookie,
      highlightedVenues,
      hasOriginB2B,
      hideFAQs,
    } = this.state;

    const {
      ResyAmexConfig: {
        baseRequestUrl,
      },
      Analytics,
      eventsHideStatus,
    } = this.props;

    const printContent = (text, key, isHTML) => {
      const errorText = `ERROR ${key}`;
      if ((!text || text === '') && isHTML) {
        return `<i class="GDADIR__ProgramDirectory__errorText">${errorText}</i>`;
      } if (!text || text === '') {
        return (<i className="GDADIR__ProgramDirectory__errorText">{errorText}</i>);
      } if (text && Array.isArray(text) && text.length === 0) {
        return (<i className="GDADIR__ProgramDirectory__errorText">{errorText}</i>);
      }
      return text;
    };

    const pageContentByLanguage = dataOutput;
    const { userLocation } = this.props;
    let isGdaMember = false;
    let isGdaEligible = false;
    let isNonUSLocation = false;
    if (userLocation) {
      isNonUSLocation = userLocation.isNonUS;
    }

    if (gdaCookie === GDA_MEMBER) {
      isGdaMember = true;
    }

    if (gdaCookie === GDA_ELIGIBLE) {
      isGdaEligible = true;
    }

    if (pageContentByLanguage) {
      const renderCities = () => {
        if (selectedCity) {
          if (loading) {
            return (
              <div>
                <Loader className="GDADIR__main__loader" />
              </div>
            );
          }
          const { bookNowLinkText = '', noBookNowText = '' } = pageContentByLanguage.featured || '';
          return (
            <div aria-live="polite">
              <h2 className="GDADIR__main__title">
                {cityCountryText}
              </h2>
              {venues.length > 0 ? (
                <VenuesGrid
                  venues={venues}
                  bookNowLinkText={printContent(bookNowLinkText, 'bookNowLinkText')}
                  noBookNowText={printContent(noBookNowText, 'noBookNowText')}
                  handleDirTagging={this.handleDirTagging}
                />
              ) : (
                <div className="GDADIR__main__no-data">
                  <p>
                    {printContent(pageContentByLanguage.noAvailableVenues, 'noAvailableVenues')}
                  </p>
                </div>
              )}
            </div>
          );
        }
        return (
          <>
            <h3>{printContent(pageContentByLanguage.pgmDirTextWordMap, 'pgmDirTextWordMap')}</h3>
            {highlightedVenues && (
              <div className="GDADIR__main__highlightedVenues">
                <VenuesMapDisplay
                  highlightedVenues={highlightedVenues}
                  baseRequestUrl={baseRequestUrl}
                  pageContentByLanguage={pageContentByLanguage}
                  handleDirTagging={this.handleDirTagging}
                />
              </div>
            )}
            {!highlightedVenues && (
              <div className="GDADIR__main__no-data" aria-live="polite">
                <p>{printContent(pageContentByLanguage.noCitySelected, 'noCitySelected')}</p>
              </div>
            )}
          </>
        );
      };

      let imageUrl = `${baseRequestUrl}GDA/images/ImageNotAvailable.svg`;
      if (pageContentByLanguage.dirPageExploreImg && pageContentByLanguage.dirPageExploreImg !== '') {
        imageUrl = `${baseRequestUrl}GDA/images/${pageContentByLanguage.dirPageExploreImg}`;
      }

      const sanitizedExplainerBlurb = sanitizeString(pageContentByLanguage.explainerBlurb);
      const sanitizedExplainerBlurbGda = sanitizeString(pageContentByLanguage.explainerBlurbGda);
      const context = utils.getPageContext(window?.location?.pathname);

      const benefitsExtraParams = {
        // eslint-disable-next-line camelcase
        action_name: 'Explore Your Benefits Click',
      };

      return (
        <div className="GDA container">
          <main>
            <AmexGDADirHeader
              menuItems={pageContentByLanguage.menuItemsDir}
              mobileMenuContent={pageContentByLanguage.mobileMenuContent}
              headerExplore={pageContentByLanguage.headerExplore}
              baseRequestUrl={baseRequestUrl}
              headerTextImg={pageContentByLanguage.headerTextImg}
              amexFooterImg={pageContentByLanguage.amexFooterImg}
              printContent={printContent}
              isGdaMember={isGdaMember}
              isGdaEligible={isGdaEligible}
              isUSLocation={!isNonUSLocation}
              hideEvents={eventsHideStatus}
              hasOriginB2B={hasOriginB2B}
              Analytics={Analytics}
              hideFAQs={hideFAQs}
            />
            <section className="GDA__section GDADIR__ProgramDirectory">
              <div className="GDADIR__ProgramDirectory__Wrap">
                <h1 className="GDADIR__ProgramDirectory__headline">
                  {!isGdaMember && printContent(pageContentByLanguage.explainerHeadline, 'explainerHeadline')}
                  {isGdaMember && printContent(pageContentByLanguage.explainerHeadlineGda, 'explainerHeadlineGda')}
                </h1>
                {!isGdaMember
                  && (
                    <p
                      // eslint-disable-next-line react/no-danger
                      dangerouslySetInnerHTML={{ __html: printContent(sanitizedExplainerBlurb, 'explainerBlurb', true) }}
                    />
                  )}
                {isGdaMember
                  && (
                    <p
                      // eslint-disable-next-line react/no-danger
                      dangerouslySetInnerHTML={{ __html: printContent(sanitizedExplainerBlurbGda, 'explainerBlurbGda', true) }}
                    />
                  )}
              </div>
              <nav className="GDADIR__ProgramDirectory__Breadcrumb" aria-label="Breadcrumb">
                <Link
                  className="GDADIR__ProgramDirectory__Breadcrumb--home"
                  href={updateLinksWithOriginB2B('/global-dining-access', hasOriginB2B)}
                >
                  {printContent(pageContentByLanguage.explainerHome, 'explainerHome')}
                </Link>
                <Link
                  className="GDADIR__ProgramDirectory__Breadcrumb--current"
                  href={updateLinksWithOriginB2B('/global-dining-access/directory', hasOriginB2B)}
                  aria-current="page"
                >
                  {printContent(pageContentByLanguage.explainerHeadline, 'explainerHeadline')}
                </Link>
              </nav>
            </section>
            <section className="GDA__section">
              <div className="GDADIR__main">
                {locations.length > 0 ? (
                  <>
                    <div className="GDADIR__main__selector">
                      <h3>{printContent(pageContentByLanguage.pgmDirTextCityNames, 'pgmDirTextCityNames')}</h3>
                      <CountryCitySelector
                        allCities={locations}
                        callCity={this.callCity}
                        selectorContent={pageContentByLanguage.selectorContent}
                        proximity={proximity}
                        printContent={printContent}
                        handleDirTagging={this.handleDirTagging}
                      />
                    </div>
                    <div className="GDADIR__main__grid">
                      {renderCities()}
                    </div>
                  </>
                ) : (
                  <div className="GDADIR__main__no-data" aria-live="polite">
                    <p>
                      {printContent(pageContentByLanguage.noAvailableRestaurants, 'noAvailableRestaurants')}
                    </p>
                  </div>
                )}
              </div>
            </section>
            <br />
            <br />
            <section className="GDA__section">
              <div
                className="GDADIR__ExploreBg"
                style={{
                  backgroundImage: `url(${imageUrl})`,
                }}
              >
                <div className="GDADIR__ExploreContent">
                  <p className="GDADIR__ExploreContent--text">
                    {!isGdaMember && printContent(pageContentByLanguage.exploreTheProgramText, 'exploreTheProgramText')}
                    {isGdaMember && printContent(pageContentByLanguage.exploreTheProgramTextGda, 'exploreTheProgramTextGda')}
                  </p>
                  <Link
                    className="GDADIR__ExploreContent--CTA"
                    href={updateLinksWithOriginB2B('/global-dining-access/#benefits', hasOriginB2B)}
                    onContextMenu={() => this.handleDirTagging({ ...benefitsExtraParams, context })}
                    onClick={() => this.handleDirTagging({ ...benefitsExtraParams, context })}
                  >
                    {printContent(pageContentByLanguage.exploreTheProgramCTA, 'exploreTheProgramCTA')}
                  </Link>
                </div>
              </div>
            </section>
            <br />
            <AmexGDAFooter
              scrollTo={this.scrollTo}
              footer={pageContentByLanguage.footer}
              amexFooterImg={pageContentByLanguage.amexFooterImg}
              baseRequestUrl={baseRequestUrl}
              androidAppLink={pageContentByLanguage.androidAppLink}
              iOsAppLink={pageContentByLanguage.iOsAppLink}
              printContent={printContent}
              hasOriginB2B={hasOriginB2B}
              Analytics={Analytics}
              isDirectoryPage
            />
          </main>
        </div>
      );
    }
    return null;
  }
}

const mapStateToProps = (state) => ({
  venues: state.collections.venues.marketing,
  eventsHideStatus: state.amexGDAEvents?.gdaEventsHideStatus,
  userLocation: state.ipdata,
});

AmexGDADir.displayName = 'AmexGDADir';
export { AmexGDADir as ComponentAmexGDADirUnderTest };
export { AmexGDADir as ComponentUnderTest };
export default connect(mapStateToProps)(AmexGDADir);
